import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLateral from "../../Componentes/MenuLateral";
import { projectApi } from "../../Services/api";
import "./styles.css";

function getLoggedUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("usuarioLogado"));
    const userId = Number(user?.IDusuario);
    return Number.isInteger(userId) && userId > 0 ? userId : null;
  } catch (error) {
    return null;
  }
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.errors?.default || fallback;
}

export default function Configuracoes() {
  const navigate = useNavigate();
  const userId = useMemo(() => getLoggedUserId(), []);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [stage2Enabled, setStage2Enabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (!userId) {
      navigate("/");
    }
  }, [navigate, userId]);

  useEffect(() => {
    if (!expiresAt) {
      setSecondsRemaining(0);
      return undefined;
    }

    const updateRemainingTime = () => {
      const remaining = Math.max(
        0,
        Math.ceil((expiresAt - Date.now()) / 1000)
      );
      setSecondsRemaining(remaining);

      if (remaining === 0) {
        setToken("");
        setExpiresAt(null);
      }
    };

    updateRemainingTime();
    const interval = window.setInterval(updateRemainingTime, 1000);
    return () => window.clearInterval(interval);
  }, [expiresAt]);

  async function loadProtectedConfiguration(nextToken) {
    const response = await projectApi.get("/configuracoes", {
      headers: { Authorization: `Bearer ${nextToken}` },
    });
    const feature = response?.data?.configuracoes?.find(
      (item) => item.chave === "MICRO_ROTEAMENTO_ETAPA_2_HABILITADA"
    );
    setStage2Enabled(Boolean(feature?.valor));
  }

  async function handleAuthenticate(event) {
    event.preventDefault();
    if (!password || !userId) {
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await projectApi.post("/configuracoes/autenticar", {
        senha: password,
        IDusuario: userId,
      });
      const nextToken = response?.data?.token || "";
      const expiresInSeconds = Number(
        response?.data?.expiresInSeconds || 900
      );

      await loadProtectedConfiguration(nextToken);
      setToken(nextToken);
      setExpiresAt(Date.now() + expiresInSeconds * 1000);
      setPassword("");
      setSuccessMessage("Acesso administrativo liberado temporariamente.");
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel autenticar a configuracao.")
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(event) {
    const nextEnabled = event.target.checked;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await projectApi.put(
        "/configuracoes/micro-roteamento-etapa-2",
        {
          habilitada: nextEnabled,
          IDusuarioAtualizacao: userId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStage2Enabled(
        Boolean(response?.data?.microRoteamentoEtapa2Habilitada)
      );
      setSuccessMessage("Configuracao global atualizada.");
    } catch (error) {
      if (error?.response?.status === 401) {
        setToken("");
        setExpiresAt(null);
      }
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel salvar a configuracao.")
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-page">
      <MenuLateral />
      <main className="settings-content">
        <section className="settings-card">
          <div className="settings-heading">
            <span>Administracao</span>
            <h1>Configuracoes do roteamento</h1>
            <p>
              A etapa 1 permanece ativa por padrao. A etapa 2 libera
              geocodificacao, marcadores e otimizacao tecnica.
            </p>
          </div>

          {!token ? (
            <form className="settings-form" onSubmit={handleAuthenticate}>
              <label htmlFor="settings-password">Senha de configuracao</label>
              <input
                id="settings-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                maxLength={500}
              />
              <button type="submit" disabled={loading || !password}>
                {loading ? "Validando..." : "Entrar"}
              </button>
            </form>
          ) : (
            <div className="settings-protected">
              <div className="settings-session">
                <span>Sessao administrativa temporaria</span>
                <strong>{secondsRemaining}s restantes</strong>
              </div>

              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={stage2Enabled}
                  onChange={handleToggle}
                  disabled={saving}
                />
                <span>
                  <strong>Habilitar etapa 2 do micro-roteamento</strong>
                  <small>
                    Afeta globalmente os controles tecnicos e o historico
                    microrroteirizado.
                  </small>
                </span>
              </label>
            </div>
          )}

          {errorMessage ? <p className="settings-error">{errorMessage}</p> : null}
          {successMessage ? (
            <p className="settings-success">{successMessage}</p>
          ) : null}
        </section>
      </main>
    </div>
  );
}
